import { AuthorizationWrapper } from '@frontend/authorization';
import { EntityType, Rights } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { Application } from '@frontend/functionality/types';
import { getApplicationDisplayName } from '@frontend/functionality/utils';
import { CommonMessage } from '@frontend/lang';
import { ReportClient } from '@frontend/report/api';
import { Report } from '@frontend/report/types';
import { type ReactNode } from 'react';

import OverviewHeader from '../../applications/header/overview-header';
import { usePlatformModalHost } from '../../modal/platform-modal-host/platform-modal-host.context';
import Unauthorized from '../../unauthorized/unauthorized';
import { CardList } from '../card-list/card-list.component';
import { TagFilterContainer } from '../card-tag-filter-container/tag-filter-container.component';
import useReportsOverview from './report-overview.controller';

const REPORT_TAB_DISPLAY_MAP = new Map<string, ReactNode>([
    [Application.CATALOGUE, getApplicationDisplayName(Application.CATALOGUE)],
    [Application.LAUNDRY, getApplicationDisplayName(Application.LAUNDRY)],
    [Application.LENDING, getApplicationDisplayName(Application.LENDING)],
    [Application.NIVALIZ, getApplicationDisplayName(Application.NIVALIZ)],
    [Application.NOTIFICATIONS, getApplicationDisplayName(Application.NOTIFICATIONS)],
    [Application.PUDO, getApplicationDisplayName(Application.PUDO)],
    [Application.USERS, getApplicationDisplayName(Application.USERS)],
    [Application.VENDING, getApplicationDisplayName(Application.VENDING)]
]);

const ID = 'report-overview';

const ReportsOverview = () => {
    const viewProps = useReportsOverview();
    const { openPlatformDetail } = usePlatformModalHost();

    if (!viewProps.isReady) {
        return (
            <div
                className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'
                id={ID}>
                <OverviewHeader
                    title={
                        <div className='inline-flex gap-2'>
                            {getApplicationDisplayName(Application.ANALYTICS)} / {CommonMessage.OBJECTS.REPORT.PLURAL}
                        </div>
                    }
                />
                <div className='inline-flex w-full flex-col items-start justify-start gap-3'>
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'
            id={ID}>
            <AuthorizationWrapper
                showUnauthorized
                unauthorizedContent={<Unauthorized />}
                requiredPermissions={[{ item: EntityType.REPORT, source: EntityType.ACCOUNT, right: Rights.READ, uid: viewProps.currentAccount! }]}>
                <OverviewHeader
                    title={
                        <div className='inline-flex gap-2'>
                            {getApplicationDisplayName(Application.ANALYTICS)} / {CommonMessage.OBJECTS.REPORT.PLURAL}
                        </div>
                    }
                />
                <div className='inline-flex w-full flex-col items-start justify-start gap-3'>
                    {viewProps.showTabs && (
                        <TagFilterContainer
                            tabs={viewProps.tabs}
                            onChange={viewProps.onChange}
                            activeTab={viewProps.activeTab ?? ''}
                            displayMap={REPORT_TAB_DISPLAY_MAP}
                        />
                    )}
                    {viewProps.activeTab && (
                        <CardList<Report>
                            type={EntityType.REPORT}
                            dispatch={viewProps.dispatch}
                            useStaticListId
                            eventListener={EntityType.REPORT}
                            id={viewProps.reportListId}
                            fetch={(arg) =>
                                ReportClient.fetchReports({
                                    account_id: viewProps.currentAccount,
                                    type: reportTypeFilter(viewProps),
                                    ...arg
                                })
                            }
                            onSelectCard={(obj) =>
                                openPlatformDetail({
                                    entityType: EntityType.REPORT,
                                    id: obj.id
                                })
                            }
                            cardOptions={(item) => ({
                                cardClassName:
                                    'bg-background-surface cursor-pointer outline-border-default inline-flex h-64 cursor-pointer flex-col items-start justify-between self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px] hover:shadow-lg',
                                tags: Object.keys(viewProps.reportApplicationTemplate ?? {}).filter((key) =>
                                    viewProps.reportApplicationTemplate?.[key]?.some((type) => type === item.type)
                                ),
                                tagDisplayMap: REPORT_TAB_DISPLAY_MAP
                            })}
                        />
                    )}
                </div>
            </AuthorizationWrapper>
        </div>
    );
};

export default ReportsOverview;

const reportTypeFilter = (viewProps: ReturnType<typeof useReportsOverview>): string[] => {
    if (!viewProps.activeTab || !viewProps.reportApplicationTemplate) return [];
    return viewProps.reportApplicationTemplate[viewProps.activeTab] ?? [];
};
